import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import {
  Box,
  FormControl,
  Grid2,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { Edit } from "@mui/icons-material";
import { Datepicker, locale, MbscCalendarEvent, MbscPopupButton } from "@mobiscroll/react";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import useTimeFormat from "../../hooks/useTimeFormat/useTimeFormat";
import { updateTimes } from "./functions/schedule.functions";
import { BookingType } from "../../features/Booking-Form/typings/booking-inputs";
import { BookingScheduleInterface } from "../../features/Booking-Form/typings/booking.types";
import { UpdateSeriesBookingTimesResponse } from "../../features/Schedule/schedule.types";
import {
  SeriesBookingEditMode,
  SeriesBookingScope
} from "../../features/Schedule/series-booking.types";
import { validTimeRange } from "../BookingForm/functions/date-time.functions";
import useTimezone from "../../hooks/useTimezone/useTimezone";
import {
  isVehicleScheduleEndEditable,
  isVehicleScheduleStartEditable
} from "../FleetManagement/EventDetailDialog/functions/isEventEditable.function";
import { bookingHasSeriesId } from "./functions/booking-has-series-id.function";
import { ScheduleEventTimeVehiclePartial } from "./partial/schedule-event-time-vehicle.partial";
import { ScheduleSeriesEditConflictDialog } from "./schedule-series-edit-conflict-dialog.component";
import { ScheduleSeriesScopeDialog } from "./schedule-series-scope-dialog.component";
import { ScheduleSeriesBookingIndicator } from "./schedule-series-booking-indicator.component";

export const editStyles = (isMobile: boolean) => {
  return {
    parentPickerStyle: [isMobile ? { minWidth: "14rem" } : { minWidth: "fit-content" }],
    pickerChildStyle: { flexWrap: isMobile ? "wrap" : "nowrap" },
    endTimePickerStyle: [isMobile ? { width: "13rem" } : { width: "17rem" }]
  };
};

export const formattedTimeByBookingType = ({
  startDate,
  endDate,
  timezone,
  timeFormat,
  isVehicleBooking
}: {
  startDate: string;
  endDate: string;
  timezone: string;
  timeFormat: string;
  isVehicleBooking: boolean;
}) => {
  if (isVehicleBooking) {
    return {
      start: startDate,
      end: endDate
    };
  }
  return {
    start: DateTime.fromISO(startDate).setZone(timezone).toFormat(timeFormat),
    end: DateTime.fromISO(endDate).setZone(timezone).toFormat(timeFormat)
  };
};

export type InlineTimeEdit = {
  isEditing: boolean;
  valueBefore: {
    start: string;
    end: string;
  };
  value: {
    start: string;
    end: string;
  };
};

type P = {
  schedule: BookingScheduleInterface;
  calendarData: MbscCalendarEvent[];
  isEditable: boolean;
  isColleagueScheduleEditable: boolean;
  /** When true, hides inline time edit (same window as cancel booking; set at dialog / popover). */
  isConferenceServiceSetupLock?: boolean;
  setCalendarData?: Dispatch<SetStateAction<MbscCalendarEvent[]>>;
  refetchAllSchedule?: () => void;
  /**
   * When true, parent booking dialog should ignore backdrop/Escape close
   * (time picker open, or series scope/conflict dialog open).
   */
  onBlockingUiChange?: (isBlocking: boolean) => void;
};

export const ScheduleEventTime: React.FC<P> = ({
  schedule,
  calendarData,
  isEditable,
  isColleagueScheduleEditable,
  isConferenceServiceSetupLock = false,
  setCalendarData,
  refetchAllSchedule,
  onBlockingUiChange
}) => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    userInformation: { sub: userInformationSub },
    settings: { timezone: timezoneState }
  } = useSelector((state: RootState) => state.login);

  const { timeFormat, dateFormat } = useTimeFormat();

  const deviceTimezone = useTimezone();
  // if the schedule's timezone isn't the users default, render users timezone
  const timezone = timezoneState === schedule.timezone ? schedule.timezone : deviceTimezone;

  const isVehicleBooking =
    schedule.bookingType === BookingType.VEHICLE || Boolean(schedule.vehicleInfo);

  const { start: formattedStartTime, end: formattedEndTime } = formattedTimeByBookingType({
    startDate: schedule.startDate,
    endDate: schedule.endDate,
    timezone,
    timeFormat,
    isVehicleBooking
  });

  const [editTime, setEditTime] = useState<InlineTimeEdit>({
    isEditing: false,
    valueBefore: {
      start: formattedStartTime,
      end: formattedEndTime
    },
    value: {
      start: formattedStartTime,
      end: formattedEndTime
    }
  });
  const [seriesScopeDialogOpen, setSeriesScopeDialogOpen] = useState(false);
  const [seriesConflict, setSeriesConflict] = useState<UpdateSeriesBookingTimesResponse | null>(
    null
  );
  const [pendingSeriesTimes, setPendingSeriesTimes] = useState<{
    newStart: string;
    newEnd: string;
  } | null>(null);
  const [pendingSeriesScope, setPendingSeriesScope] = useState<SeriesBookingScope | null>(null);
  /**
   * While the series scope/conflict flow is active, ignore Mobiscroll Datepicker `onClose`
   * re-fires (closing `isEditing` triggers onClose and would reopen the scope dialog).
   */
  const ignoreTimepickerCloseRef = useRef(false);

  const isSeries = bookingHasSeriesId(schedule.seriesId);

  // Same editable logic as Fleet table: end in future; start also requires !checkedIn
  const isEndTimeInTheFuture = isVehicleScheduleEndEditable(schedule, timezone);
  const isVehicleStartEditable =
    isVehicleBooking && isVehicleScheduleStartEditable(schedule, timezone);

  // Own/colleague: lock start once past. Company-admin colleague-editable (#1577) may still edit start.
  const onlyEditEndTime =
    !isColleagueScheduleEditable && DateTime.fromISO(schedule.startDate) <= DateTime.now();

  const editStartDt = DateTime.fromISO(editTime.value.start).setZone(timezone);
  const editEndDt = DateTime.fromISO(editTime.value.end).setZone(timezone);

  const canEditTime =
    ((isEditable && schedule.userBookedFor === userInformationSub) ||
      isColleagueScheduleEditable) &&
    !editTime.isEditing &&
    (isColleagueScheduleEditable || isEndTimeInTheFuture) &&
    !isConferenceServiceSetupLock;

  const confirmChangesOnClose = async () => {
    if (ignoreTimepickerCloseRef.current) {
      return;
    }

    // if time is not changed, then don't trigger a request
    if (
      editTime.value.start === editTime.valueBefore.start &&
      editTime.value.end === editTime.valueBefore.end
    ) {
      setEditTime(e => ({ ...e, isEditing: false }));
    } else if (isSeries) {
      ignoreTimepickerCloseRef.current = true;
      setPendingSeriesTimes({
        newStart: editTime.value.start,
        newEnd: editTime.value.end
      });
      // Close the time picker before opening the scope dialog so its onClose cannot reopen it.
      setEditTime(e => ({ ...e, isEditing: false }));
      setSeriesScopeDialogOpen(true);
    } else {
      onScheduleTimeUpdate({
        newStart: editTime.value.start,
        newEnd: editTime.value.end,
        scheduleId: schedule.id
      });
    }
  };

  const cancelChanges = () => {
    ignoreTimepickerCloseRef.current = false;
    setEditTime(e => ({
      ...e,
      value: { start: editTime.valueBefore.start, end: editTime.valueBefore.end },
      isEditing: false
    }));
    setPendingSeriesTimes(null);
    setPendingSeriesScope(null);
    setSeriesScopeDialogOpen(false);
    setSeriesConflict(null);
  };

  const isBlockingParentClose =
    editTime.isEditing || seriesScopeDialogOpen || seriesConflict != null;

  useEffect(() => {
    onBlockingUiChange?.(isBlockingParentClose);
    return () => onBlockingUiChange?.(false);
  }, [isBlockingParentClose, onBlockingUiChange]);

  async function onScheduleTimeUpdate({
    newStart,
    newEnd,
    scheduleId,
    seriesScope,
    seriesMode,
    onConflict
  }: {
    newStart: string;
    newEnd: string;
    scheduleId: number;
    seriesScope?: SeriesBookingScope;
    seriesMode?: SeriesBookingEditMode;
    onConflict?: (conflict: UpdateSeriesBookingTimesResponse) => void;
  }) {
    let newStartIso: string;
    let newEndIso: string;

    if (isVehicleBooking) {
      newStartIso = DateTime.fromISO(newStart).toISO() || DateTime.now().toISO();
      newEndIso = DateTime.fromISO(newEnd).toISO() || DateTime.now().toISO();
    } else {
      const newStartTime = DateTime.fromISO(newStart);
      const newEndTime = DateTime.fromISO(newEnd);
      const scheduleDate = DateTime.fromISO(schedule.start || schedule.startDate);

      newStartIso =
        scheduleDate.set({ hour: newStartTime.hour, minute: newStartTime.minute }).toISO() ||
        DateTime.now().toISO();
      newEndIso =
        scheduleDate.set({ hour: newEndTime.hour, minute: newEndTime.minute }).toISO() ||
        DateTime.now().toISO();
    }

    await updateTimes(
      {
        bookingId: scheduleId,
        newStartTime: newStartIso,
        newEndTime: newEndIso,
        timezone,
        ...(isColleagueScheduleEditable ? { colleagueId: schedule.userBookedFor } : {})
      },
      schedule,
      calendarData,
      { enqueueSnackbar, setCalendarData, refetchAllSchedule, cancelChanges },
      seriesScope
        ? {
            scope: seriesScope,
            mode: seriesMode ?? SeriesBookingEditMode.ALL_OR_NOTHING,
            onConflict
          }
        : undefined
    ).then(outcome => {
      if (outcome === "success") {
        ignoreTimepickerCloseRef.current = false;
        setEditTime(e => ({
          ...e,
          valueBefore: { start: newStart, end: newEnd },
          isEditing: false
        }));
        setPendingSeriesTimes(null);
        setPendingSeriesScope(null);
        setSeriesScopeDialogOpen(false);
        setSeriesConflict(null);
      }
    });
  }

  const handleSeriesScopeConfirm = (scope: SeriesBookingScope) => {
    setSeriesScopeDialogOpen(false);
    if (!pendingSeriesTimes) {
      return;
    }
    setPendingSeriesScope(scope);
    onScheduleTimeUpdate({
      newStart: pendingSeriesTimes.newStart,
      newEnd: pendingSeriesTimes.newEnd,
      scheduleId: schedule.id,
      seriesScope: scope,
      onConflict: conflict => setSeriesConflict(conflict)
    });
  };

  const handleConflictUpdateFreeDays = () => {
    if (!pendingSeriesTimes || pendingSeriesScope == null) {
      cancelChanges();
      return;
    }
    const scope = pendingSeriesScope;
    setSeriesConflict(null);
    onScheduleTimeUpdate({
      newStart: pendingSeriesTimes.newStart,
      newEnd: pendingSeriesTimes.newEnd,
      scheduleId: schedule.id,
      seriesScope: scope,
      seriesMode: SeriesBookingEditMode.SKIP_CONFLICTS
    });
  };

  const handleConflictOnlySelectedDay = () => {
    if (!pendingSeriesTimes) {
      return;
    }
    setSeriesConflict(null);
    onScheduleTimeUpdate({
      newStart: pendingSeriesTimes.newStart,
      newEnd: pendingSeriesTimes.newEnd,
      scheduleId: schedule.id,
      seriesScope: SeriesBookingScope.OCCURRENCE
    });
  };

  const selectedDayIsFree = seriesConflict?.freeBookingIds?.includes(schedule.id) === true;

  const timePickingButtons: MbscPopupButton[] = [
    {
      name: "cancel",
      handler: cancelChanges,
      text: t("_datePickerButtonsCancel")
    },
    {
      name: "set",
      handler: () => {
        void confirmChangesOnClose();
      },
      text: t("_datePickerButtonsSet")
    }
  ];

  return (
    <Grid2 size={{ xs: 12 }} sx={{ display: "flex", alignItems: "center" }}>
      <Box>
        <Grid2 container data-testid="schedule-event-time-par" display={"grid"}>
          <Box>
            {!(isVehicleBooking && editTime.isEditing) && (
              <Typography data-testid="schedule-item-time">
                {isVehicleBooking
                  ? `${editStartDt.toFormat(dateFormat)} – ${editEndDt.toFormat(dateFormat)}`
                  : DateTime.fromISO(schedule.startDate).toFormat(dateFormat)}
              </Typography>
            )}
            <Grid2 display={"flex"}>
              {!editTime.isEditing && (
                <Typography data-testid="schedule-item-time" sx={{ mt: 0.2 }}>
                  {isVehicleBooking
                    ? `${editStartDt.toFormat(timeFormat)} - ${editEndDt.toFormat(timeFormat)}`
                    : `${editTime.value.start} - ${editTime.value.end}`}
                </Typography>
              )}
              {canEditTime && (
                <IconButton
                  onClick={e => {
                    e.stopPropagation();
                    setEditTime(e => ({ ...e, isEditing: true }));
                  }}
                  data-testid="edit-schedule-item-time"
                  sx={{ margin: "0 8px", padding: 0 }}
                  size="large"
                >
                  <Edit fontSize="small" />
                </IconButton>
              )}
            </Grid2>
          </Box>
          <Grid2
            sx={{ display: "flex", alignItems: "flex-end", mb: "3px" }}
            data-testid="schedule-item-time-change-buttons"
          >
            {/* editing the booking schedule time */}
            {editTime.isEditing && (
              <Grid2
                size={{ xs: 12 }}
                sx={editStyles(isMobile).parentPickerStyle}
                data-testid="schedule-item-time-change-date-picker"
              >
                {isVehicleBooking ? (
                  <Grid2
                    container
                    display="flex"
                    sx={editStyles(isMobile).pickerChildStyle}
                    gap={0.5}
                    alignItems="center"
                  >
                    <ScheduleEventTimeVehiclePartial
                      editTime={editTime}
                      setEditTime={setEditTime}
                      isVehicleStartEditable={isVehicleStartEditable}
                      confirmChangesOnClose={confirmChangesOnClose}
                      cancelChanges={cancelChanges}
                    />
                  </Grid2>
                ) : (
                  <FormControl
                    fullWidth
                    data-testid="timepicker-control"
                    sx={{ margin: "-25px -0px -20px -18px" }}
                  >
                    {/* edit both start and end time */}
                    {!onlyEditEndTime && (
                      <Datepicker
                        select={"range"}
                        data-testid={"timePicker"}
                        controls={["time"]}
                        touchUi={isMobile}
                        buttons={isMobile ? timePickingButtons : []}
                        timeFormat={timeFormat}
                        isOpen={editTime.isEditing}
                        locale={locale[i18n.language]}
                        onClose={confirmChangesOnClose}
                        onTempChange={picker => {
                          // when browser prohibit temp change
                          if (!isMobile) return;

                          if (picker.value && Array.isArray(picker.value)) {
                            const newStart = DateTime.fromJSDate(picker.value[0] as Date)
                              .setZone(timezone)
                              .toFormat("HH:mm");
                            const newEnd = DateTime.fromJSDate(picker.value[1] as Date)
                              .setZone(timezone)
                              .toFormat("HH:mm");

                            setEditTime(e => ({ ...e, value: { start: newStart, end: newEnd } }));
                          }
                        }}
                        onChange={picker => {
                          if (picker.value && Array.isArray(picker.value)) {
                            const newStart = DateTime.fromJSDate(picker.value[0] as Date)
                              .setZone(timezone)
                              .toFormat("HH:mm");
                            const newEnd = DateTime.fromJSDate(picker.value[1] as Date)
                              .setZone(timezone)
                              .toFormat("HH:mm");

                            setEditTime(e => ({ ...e, value: { start: newStart, end: newEnd } }));
                          }
                        }}
                        value={[editTime.value.start, editTime.value.end]}
                        stepMinute={15}
                        valid={[
                          {
                            start: validTimeRange(schedule.startDate),
                            end: "23:59"
                          }
                        ]}
                      />
                    )}

                    {/* if start time is in the past than the now_time, can only edit the end time */}
                    {onlyEditEndTime && (
                      <Grid2
                        container
                        display={"flex"}
                        flexWrap={"nowrap"}
                        sx={editStyles(isMobile).endTimePickerStyle}
                      >
                        <Datepicker
                          disabled
                          data-testid={"start-timePicker"}
                          controls={["time"]}
                          timeFormat={timeFormat}
                          locale={locale[i18n.language]}
                          value={editTime.value.start}
                        />
                        <Datepicker
                          data-testid={"end-timePicker"}
                          controls={["time"]}
                          timeFormat={timeFormat}
                          locale={locale[i18n.language]}
                          isOpen={editTime.isEditing}
                          touchUi={isMobile}
                          buttons={isMobile ? timePickingButtons : []}
                          onClose={confirmChangesOnClose}
                          onTempChange={picker => {
                            // when browser prohibit temp change
                            if (!isMobile) return;

                            if (picker.value) {
                              const origStart = DateTime.fromISO(schedule.startDate)
                                .setZone(timezone)
                                .toFormat("HH:mm");
                              const newEnd = DateTime.fromJSDate(picker.value as Date)
                                .setZone(timezone)
                                .toFormat("HH:mm");

                              setEditTime(e => ({
                                ...e,
                                value: { start: origStart, end: newEnd }
                              }));
                            }
                          }}
                          onChange={picker => {
                            if (picker.value) {
                              const origStart = DateTime.fromISO(schedule.startDate)
                                .setZone(timezone)
                                .toFormat("HH:mm");
                              const newEnd = DateTime.fromJSDate(picker.value as Date)
                                .setZone(timezone)
                                .toFormat("HH:mm");

                              setEditTime(e => ({
                                ...e,
                                value: { start: origStart, end: newEnd }
                              }));
                            }
                          }}
                          value={editTime.value.end}
                          stepMinute={15}
                          valid={[
                            {
                              start: validTimeRange(schedule.startDate),
                              end: "23:59"
                            }
                          ]}
                        />
                      </Grid2>
                    )}
                  </FormControl>
                )}
              </Grid2>
            )}
          </Grid2>
        </Grid2>

        <Typography sx={{ mt: 0.1 }}>{timezone}</Typography>
        <ScheduleSeriesBookingIndicator seriesId={schedule.seriesId} surface="detail" />
      </Box>

      <ScheduleSeriesScopeDialog
        open={seriesScopeDialogOpen}
        selectedDateIso={schedule.start ?? schedule.startDate}
        timezone={schedule.timezone}
        titleKey="_schedule_seriesEditScopeTitle"
        bodyKey="_schedule_seriesEditScopeBody"
        confirmLabelKey="_datePickerButtonsSet"
        onClose={() => {
          setSeriesScopeDialogOpen(false);
          cancelChanges();
        }}
        onConfirm={handleSeriesScopeConfirm}
      />

      <ScheduleSeriesEditConflictDialog
        open={seriesConflict != null}
        conflict={seriesConflict}
        selectedDateIso={schedule.start ?? schedule.startDate}
        timezone={schedule.timezone}
        selectedDayIsFree={selectedDayIsFree}
        onClose={cancelChanges}
        onUpdateFreeDays={handleConflictUpdateFreeDays}
        onOnlySelectedDay={handleConflictOnlySelectedDay}
        onChangeTimes={() => {
          ignoreTimepickerCloseRef.current = false;
          setSeriesConflict(null);
          setSeriesScopeDialogOpen(false);
          setEditTime(e => ({ ...e, isEditing: true }));
        }}
      />
    </Grid2>
  );
};
